/**
 * Safety net that keeps a walk-mode visitor from being trapped by voxel collision.
 *
 * Voxel walls are solid and a gap narrower than the player stops it (CharacterController). Voxel
 * sets are generated from the splat, so an existing scene can hold stray voxels (floaters) at
 * eye height: a spawn point, a teleport target or a waypoint can sit inside one, or inside a
 * small pocket of them with no way out that the body fits through. These helpers answer the two
 * questions the controller asks to get the visitor out again:
 *
 * - findNearestFreeSpot: where is the closest horizontal position at which the body overlaps
 *   no voxels (used when the body starts embedded: spawn, walk-mode entry, teleport, a newly
 *   activated collision set)?
 * - isEnclosed: can the body reach a point `radius` away at all (a breadth-first flood fill over
 *   positions the body fits)? If not, it is in a pocket, and the controller lets it walk through
 *   voxels until it is out.
 *
 * Pure (a free-space predicate in, positions out) so it is unit-testable without PlayCanvas.
 */

/** True when a body centred at world (x, z) (at the walker's current height) overlaps nothing. */
export type FreeSpacePredicate = (x: number, z: number) => boolean;

/**
 * The free position nearest to (cx, cz): rings of `step` spacing out to `maxRadius`, each sampled
 * every `step` along its circumference (at least 8 directions), first free sample wins. On each
 * ring the samples closest to the preferred direction (dx, dz) (the way the walker faces) come
 * first, so a body that starts inside a small clump ends up past it rather than behind it. Null
 * when nothing within `maxRadius` is free.
 */
export function findNearestFreeSpot(
    cx: number,
    cz: number,
    isFree: FreeSpacePredicate,
    step: number,
    maxRadius: number,
    preferred: { x: number; z: number } = { x: 0, z: 1 },
): { x: number; z: number } | null {
    if (!(step > 0) || !(maxRadius > 0)) return null;
    if (isFree(cx, cz)) return { x: cx, z: cz };
    const startAngle = Math.hypot(preferred.x, preferred.z) > 1e-9 ? Math.atan2(preferred.x, preferred.z) : 0;
    const rings = Math.ceil(maxRadius / step);
    for (let ring = 1; ring <= rings; ring++) {
        const r = Math.min(ring * step, maxRadius);
        const samples = Math.max(8, Math.ceil((2 * Math.PI * r) / step));
        for (let i = 0; i < samples; i++) {
            // 0, +1, -1, +2, -2, … samples away from the preferred direction.
            const k = i === 0 ? 0 : (i % 2 === 1 ? (i + 1) / 2 : -i / 2);
            const angle = startAngle + (k / samples) * Math.PI * 2;
            const x = cx + Math.sin(angle) * r;
            const z = cz + Math.cos(angle) * r;
            if (isFree(x, z)) return { x, z };
        }
    }
    return null;
}

/**
 * Whether the body at (cx, cz) is boxed in: a 4-connected flood fill over a `step` grid of free
 * positions, started at (cx, cz) (which counts as free even when the body overlaps something
 * there), never reaches the edge of the square of half-size `radius` around it. Positions the
 * body does not fit (a gap narrower than it, a wall) stop the fill exactly like they stop the
 * walker, so "enclosed" means "no way out that the body fits through, within `radius`".
 */
export function isEnclosed(cx: number, cz: number, isFree: FreeSpacePredicate, step: number, radius: number): boolean {
    if (!(step > 0) || !(radius > 0)) return false;
    const n = Math.max(1, Math.ceil(radius / step));
    const size = 2 * n + 1;
    const visited = new Uint8Array(size * size);
    const queue: number[] = [];
    const start = n * size + n;
    visited[start] = 1;
    queue.push(start);
    for (let head = 0; head < queue.length; head++) {
        const cell = queue[head];
        const i = (cell % size) - n;
        const j = Math.floor(cell / size) - n;
        if (Math.abs(i) === n || Math.abs(j) === n) return false; // reached the edge: a way out
        const neighbours: [number, number][] = [[i + 1, j], [i - 1, j], [i, j + 1], [i, j - 1]];
        for (const [ni, nj] of neighbours) {
            const index = (nj + n) * size + (ni + n);
            if (visited[index]) continue;
            visited[index] = 1;
            if (!isFree(cx + ni * step, cz + nj * step)) continue;
            queue.push(index);
        }
    }
    return true;
}
